import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, mergeMap } from 'rxjs/operators';
import * as uuid from "uuid";
import * as _ from 'lodash';
import { FileService } from './file.service';
import { TypeStructureReportService } from './type-structure-report.service';
import { ClassMapper } from '../models/class-mapper.model';
import { User } from '../models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  className = 'ReportService';
  currentSection;


  private _subject: BehaviorSubject<any>;
  observer: Observable<any>;

  constructor(private http: HttpClient, @Inject("environment") private environment: any,
    private fileService: FileService,
    private translateService: TranslateService,
    private cacheService: CacheService,
    private typeStructureReport: TypeStructureReportService) {
    this._subject = new BehaviorSubject<any>(null);
    this.observer = this._subject.asObservable();
  }

  comments(data) {
    this._subject.next(data);
  }

  getReportById(reportId, auditmap) {

    let params = {
      auditmap
    }
    let options = {
      params
    }
    
    return this.http.get(this.environment.base_URL + `/reports/${reportId}`, options).pipe(map((res:any) => {

        res.recipients = ClassMapper.fromJSONArray(res.recipients, User);
        res.recipientsCC = ClassMapper.fromJSONArray(res.recipientsCC, User)
        res.recipientsCCO = ClassMapper.fromJSONArray(res.recipientsCCO, User)

      return res;
    }));
  }

  getAllByJobId(jobId, lite) {
    let params = {
      jobId,
      lite
    }
    let options = {
      params
    }
    return this.http.get(this.environment.base_URL + "/reports", options).pipe(map(res => {
      _.forEach(res, (r: any) => {
        r.recipients = ClassMapper.fromJSONArray(r.recipients, User);
        r.recipientsCC = ClassMapper.fromJSONArray(r.recipientsCC, User)
        r.recipientsCCO = ClassMapper.fromJSONArray(r.recipientsCCO, User)
      })
      return res;
    }));
  }

  save(report) {
    return this.http.post(this.environment.base_URL + "/reports", report);
  }

  update(report) {
    return this.http.put(this.environment.base_URL + `/reports/${_.get(report, 'idReportPk')}`, report);
  }

  comment(commentObj) {
    return this.http.post(this.environment.base_URL + "/reports/comment", commentObj);
  }

  generate(report, type) {
    let reportCopy = _.cloneDeep(report);
    _.forEach(_.get(reportCopy, 'sections'), (s: any) => {
      s.description = s.description || "-";
      delete s['form'];
    })

    let key = `${uuid.v4()}/${_.get(report, 'name')}${type === "PDF" ? ".pdf" : ".doc"}`;
    let params = {
      type,
      key
    }
    let options = {
      params
    }

    return this.http.post(this.environment.base_URL + `/reports/${_.get(report, 'idReportPk')}/generate`, reportCopy, options)
      .pipe(mergeMap((res: any) => {
        let options = {
          responseType: "blob" as 'json'
        }
        return this.http.get(res, options);
      }));
  }

  getSnapshot(report, type) {
    let key = `${uuid.v4()}/${_.get(report, 'name')}.pdf`;
    return this.fileService.download(key, type, _.get(report, 'idReportPk'), _.get(report, 'typeDocument'));
  }

  emit(reportId) {
    return this.http.post(this.environment.base_URL + `/reports/${reportId}/emit`, null);
  }
  emitOnePager(reportId, questionnaireUsersId?: any[], language?: string) {
    let params: any = {};
    if (questionnaireUsersId && questionnaireUsersId.length > 0) {
      params.questionnaireUsersId = questionnaireUsersId;
    }
    if (language) {
      params.language = language;
    }
    const hasParams = Object.keys(params).length > 0;
    return this.http.post(
      this.environment.base_URL_GAIA + `/report/${reportId}/onePager`,
      null,
      hasParams ? { params } : {}
    );
  }


  updateWithFilesAndEmit(report, questionnaireUsersId?) {
    return this.updateWithFiles(report).pipe(mergeMap(r => {
      if (questionnaireUsersId) {
        return this.emitWithQuestionnaireUsersId(_.get(report, 'idReportPk'), questionnaireUsersId);
      } else {
        return this.emit(_.get(report, 'idReportPk'));
      }
    }))
  }

  getOnePager(reportId) {
    
    return this.http.get(this.environment.base_URL_GAIA + `/report/${reportId}/onePager`, {});
  }
  downloadOnePager(reportId) {
    return this.http.get(this.environment.base_URL_GAIA + `/report/${reportId}/download`, {});
  }

  addSection(reportId,section) {
    return this.http.post(this.environment.base_URL + `/reports/${reportId}/section`, section);
  }

  emitWithQuestionnaireUsersId(reportId, questionnaireUsersId) {
    let params = {
      questionnaireUsersId
    }
    return this.http.post(this.environment.base_URL + `/reports/${reportId}/emit`, null, { params });
  }

  getReportTemplates() {
    return this.http.get("./assets/templates/reportTemplates.json").pipe(map<any[], any[]>(res => res['TEMPLATES']));
  }

  getAllTemplates() {
    return this.typeStructureReport.getAll().pipe(mergeMap(res => {
      return this.getReportTemplates().pipe(map(jsonTemplates => {
        let sections: any = res;
        let templates = []
        templates.push(
          {
            name: "Plantilla Corporativa de Auditoría (COMPLETA)",
            sections: _.chain(sections).filter(s => {
              return _.find(jsonTemplates['SECTIONS_COMPLETA'], item => _.get(item, 'txtName') === _.get(s, 'txtName'));
            }).map(s => {
              const currentSection: any = _.find(jsonTemplates['SECTIONS_COMPLETA'], item => _.get(item, 'txtName') === _.get(s, 'txtName'));
              const typeStructure: any = _.cloneDeep(s);
              typeStructure.showToAudit = currentSection.showToAudit;
              typeStructure.order = currentSection.order;
              typeStructure.isObligatory = currentSection.isObligatory;
              typeStructure.mandatory = currentSection.mandatory;
              return {
                name: typeStructure.txtName,
                order: currentSection.order,
                typeStructureReport: typeStructure,
                obligatory: currentSection.isObligatory,
                optionalAudit: currentSection.optionalAudit,
                mandatory: currentSection.mandatory,
                showToAudit: currentSection.showToAudit,
                element: 0
              }
            }).value(),
            code: "COMPLETA",
            typeStructuresReport: sections
          },
          {
            name: "Plantilla Corporativa de Auditoría (REDUCIDA)",
            sections: _.chain(sections).filter(s => {
              return _.find(jsonTemplates['SECTIONS_REDUCIDA'], item => _.get(item, 'txtName') === _.get(s, 'txtName'));
            }).map(s => {
              const currentSection: any = _.find(jsonTemplates['SECTIONS_REDUCIDA'], item => _.get(item, 'txtName') === _.get(s, 'txtName'));
              const typeStructure: any = _.cloneDeep(s);
              typeStructure.showToAudit = currentSection.showToAudit;
              typeStructure.order = currentSection.order;
              typeStructure.isObligatory = currentSection.isObligatory;
              typeStructure.mandatory = currentSection.mandatory;
              return {
                name: typeStructure.txtName,
                order: currentSection.order,
                typeStructureReport: typeStructure,
                obligatory: currentSection.isObligatory,
                mandatory: currentSection.mandatory,
                optionalAudit: currentSection.optionalAudit,
                showToAudit: currentSection.showToAudit,
                element: 0
              }
            }).value(),
            code: "REDUCIDA",
            typeStructuresReport: sections
          },
          {
            name: "Plantilla Corporativa de Auditoría (NO NAVEGABLE)",
            sections: _.chain(sections).filter(s => {
              return _.find(jsonTemplates['SECTIONS_NO_NAVEGABLE'], item => _.get(item, 'txtName') === _.get(s, 'txtName'));
            }).map(s => {
              const currentSection: any  = _.find(jsonTemplates['SECTIONS_NO_NAVEGABLE'], item => _.get(item, 'txtName') === _.get(s, 'txtName'));
              const typeStructure: any  = _.cloneDeep(s);
              typeStructure.showToAudit = currentSection.showToAudit;
              typeStructure.order = currentSection.order;
              typeStructure.isObligatory = currentSection.isObligatory;
              typeStructure.mandatory = currentSection.mandatory;
              return {
                name: typeStructure.txtName,
                order: currentSection.order,
                typeStructureReport: typeStructure,
                optionalAudit: currentSection.optionalAudit,
                obligatory: currentSection.isObligatory,
                mandatory: currentSection.mandatory,
                showToAudit: currentSection.showToAudit,
                element: 0
              }
            }).value(),
            code: "NO_NAVEGABLE",
            typeStructuresReport: sections
          }
        )
        return templates;
      }))
    }))
  }

  updateWithFiles(report) {
    let fileToUpload;
    //check if newfile
    if (_.get(report, 'reportName') && _.isObject(_.get(report, 'reportName'))) {
      //hold the file
      fileToUpload = _.get(report, 'reportName');
      //get the filename and contenttype
      let filename = _.get(fileToUpload, 'name');
      let type = _.get(fileToUpload, 'type');
      //adapt the object to back
      report = _.set(report, 'reportName', filename);
      report = _.set(report, 'typeDocument', type);
    }
    if (fileToUpload) {
      return this.update(report).pipe(mergeMap(r => {
        return this.fileService.upload(fileToUpload, 'report', _.get(report, 'idReportPk'))
      }));
    } else {
      return this.update(report);
    }

  }

  getTranslations(language, cache = true) {
    let cacheKey = "translations" + "_" + language;
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any>(this.environment.base_URL + "/translations/" + language).pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }


}
